import React from 'react'
import { ConfirmationRoleAction } from '../../../legacy-design-system/action'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { ModifyServerSettingsByFilterDialog } from '../../server-settings/modify-server-settings-dialog-filters'
import { Role } from '@contrast-security-inc/base-types'
import { PureQueryOptions } from 'apollo-client'
import { AgentFeature } from '../../agent-features'
import { Placement } from '../../../legacy-design-system/popper'
import { ServerAggregationCategory } from '../shared'
import { ModifySingleServerSettingsDialog } from '../../server-settings/modify-server-settings-dialog-single'
import { ModifyBulkServerSettingsDialog } from '../../server-settings/modify-server-settings-dialog-bulk'
import { IconSettings } from '@contrast-security-inc/design-system-foundations'
import { ConfirmationRoleActionProps } from '../../../legacy-design-system/action/confirmation-role-action'

interface Server {
  readonly id: string
}

interface BulkActionProps extends Pick<ConfirmationRoleActionProps, 'size'> {
  readonly servers: Server[]
  readonly organizationID: string
  readonly organizationRole: Role
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>

  /**
   * True if the current user is a Protect user
   */
  readonly protect: boolean

  /**
   * The current organization's available agent features
   */
  readonly agentFeatures: AgentFeature[]

  readonly isFreemium: boolean
}

function getConfirmationProps(organizationRole: Role) {
  const requiredRole: Role = 'EDIT'
  return {
    isActionValid: true,
    requiredRole,
    icon: IconSettings,
    iconTooltipMessage: {
      id: 'title_servers_serverSettings',
      defaultMessage: 'Server Settings'
    },
    role: organizationRole,
    iconTooltipPlacement: 'top' as Placement,
    'data-testid': 'title_servers_serverSettings'
  }
}

interface SingleActionProps extends Pick<ConfirmationRoleActionProps, 'size'> {
  readonly server: Server
  readonly organizationID: string
  readonly organizationRole: Role
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly isFreemium: boolean

  /**
   * True if the current user is a Protect user
   */
  readonly protect: boolean

  /**
   * The current organization's available agent features
   */
  readonly agentFeatures: AgentFeature[]
}

export function ModifySingleServerSettingsAction({
  server,
  organizationID,
  organizationRole,
  refetchQueries,
  refresh,
  agentFeatures,
  protect,
  isFreemium,
  size
}: SingleActionProps) {
  return (
    <ConfirmationRoleAction
      size={size}
      {...getConfirmationProps(organizationRole)}
      renderDialog={({ isOpen, closeDialog }: RenderDialogProps) => {
        if (isOpen) {
          return (
            <ModifySingleServerSettingsDialog
              organizationID={organizationID}
              serverID={server.id}
              closeDialog={closeDialog}
              isOpen={isOpen}
              refetchQueries={refetchQueries}
              refresh={refresh}
              agentFeatures={agentFeatures}
              protect={protect}
              isFreemium={isFreemium}
            />
          )
        }
      }}
    />
  )
}

export interface ServersGridFilters
  extends Record<ServerAggregationCategory, string[]> {
  readonly excludedServers: string[]
  readonly filterText: string
  readonly quickFilter?: string
}

interface BulkByFilterActionProps extends BulkActionProps {
  readonly filters: ServersGridFilters

  readonly organizationRole: Role

  /**
   * True if the current user is a Protect user
   */
  readonly protect: boolean

  /**
   * The current organization's available agent features
   */
  readonly agentFeatures: AgentFeature[]

  readonly isFreemium: boolean
}

export function ModifyBulkServerSettingsAction({
  servers,
  organizationID,
  organizationRole,
  refetchQueries,
  refresh,
  protect,
  agentFeatures,
  isFreemium,
  size
}: BulkActionProps) {
  return (
    <ConfirmationRoleAction
      size={size}
      {...getConfirmationProps(organizationRole)}
      renderDialog={({ isOpen, closeDialog }: RenderDialogProps) => {
        return (
          <ModifyBulkServerSettingsDialog
            protect={protect}
            agentFeatures={agentFeatures}
            isFreemium={isFreemium}
            organizationID={organizationID}
            serverIDs={servers.map((server) => server.id)}
            closeDialog={closeDialog}
            isOpen={isOpen}
            refetchQueries={refetchQueries}
            refresh={refresh}
          />
        )
      }}
    />
  )
}

export function ModifyServerSettingsByFilterAction({
  organizationID,
  organizationRole,
  filters,
  refetchQueries,
  refresh,
  protect,
  agentFeatures,
  isFreemium,
  size
}: BulkByFilterActionProps) {
  return (
    <ConfirmationRoleAction
      size={size}
      {...getConfirmationProps(organizationRole)}
      renderDialog={({ isOpen, closeDialog }) => {
        return (
          filters &&
          filters.quickFilter && (
            <ModifyServerSettingsByFilterDialog
              protect={protect}
              agentFeatures={agentFeatures}
              organizationID={organizationID}
              isOpen={isOpen}
              closeDialog={closeDialog}
              filters={{
                quickFilter: filters.quickFilter,
                ...filters
              }}
              refetchQueries={refetchQueries}
              refresh={refresh}
              isFreemium={isFreemium}
            />
          )
        )
      }}
    />
  )
}
